import log from "loglevel";
import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { getHostInstanceManagerApi } from "../../externalApi/InstanceManagerApi";
import { lazyLoadModule } from "../../lazyLoadModule";
import { Data } from "../orchestrator/VisibilityTelemetry";
import { WidgetApi } from "../orchestrator/WidgetApi";
import { getWidgetOrchestrator } from "../orchestrator/orchestrator";
import { getUriForResource } from "../registrar/registrarHelpers";
import { DispatchOnly } from "../../../telemetry/DispatchOnly";
import * as Constants from "../../constants";
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../telemetry/Telemetry");
export class WidgetInstance {
    constructor(registration, widgetId, // Generated id provided by the platform
    instanceId, widgetInstanceId, indexedDBInstanceId, widgetDesignation, cachedProperties, firstRunCreation) {
        Trace.logEvent(dashboard, "WebAppWidgetInstanceCreated", 0 /* verbose */, {
            appId: registration.appId,
            providerId: registration.providerId,
            widgetDefinitionId: registration.id,
            widgetInstanceId: widgetId,
        });
        this._loadingCompletePromise = null;
        this._loadingCompletePromiseResolver = null;
        this._loadingStartedPromiseResolver = null;
        this._loadingStartedPromise = new Promise((resolve) => (this._loadingStartedPromiseResolver = resolve));
        this._widgetRegistration = registration;
        this._instanceId = instanceId;
        this._widgetId = widgetId;
        this._widgetInstanceId = widgetInstanceId;
        this._indexedDBInstanceId = indexedDBInstanceId;
        this._accountId = "";
        this._apiObjects = new Map();
        this._isLoadForAddedInstancePending = false;
        this._isCustomElementDefined = false;
        if (widgetDesignation === undefined) {
            this._widgetDesignation = "widget";
        }
        else {
            this._widgetDesignation = widgetDesignation;
        }
        if (cachedProperties === undefined) {
            this._cachedProperties = new Map();
            if (this._widgetRegistration.defaults !== undefined && firstRunCreation) {
                // Eventually these defaults cannot come from individual widgetRegistrations and the defaults property should be removed
                // from IWidgetRegistration, as that does not scale to supporting multiple default instances for any particular widget registration.
                let defaults = this._widgetRegistration.defaults;
                this._cachedProperties.set("column", defaults.column);
                this._cachedProperties.set("size", defaults.defaultSize);
                this._cachedProperties.set("position", defaults.position);
            }
            else {
                // When no default is given and we have no cached properties, we only need to set the size as preferredSize
                this._cachedProperties.set("size", this._widgetRegistration.preferredSize);
            }
        }
        else {
            // widgets may choose to drop support for their size on upgrade by updating their registration
            // rectify that here
            const currentSize = cachedProperties.get("size");
            const isSizeSupported = registration.supportedSizes.includes(currentSize);
            if (!isSizeSupported) {
                cachedProperties.set("size", registration.preferredSize);
            }
            this._cachedProperties = cachedProperties;
        }
        this._stateChangeListeners = new Array();
        this._executeActionListeners = new Array();
        this._submitActionListeners = new Array();
        this._widgetState = "unknown";
        this._widgetErrorStateReason = "";
        this._widgetErrorCode = 0;
        this._telemetry = new Data.Telemetry();
        this._softRefreshRequested = false;
        this._boardView = "";
        // Widget icon isn't tied to state (size, title, attribution, and status are) and should not be updatable;
        //  we can store the iconUri once and use it without checking for updates
        this._widgetIconUri = getUriForResource(this.widgetRegistration.id, this._widgetRegistration.icon.src);
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WidgetInstanceInitialized", 16777216 /* productAndServicePerformance */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this._indexedDBInstanceId,
                instanceId: this._instanceId,
                isTombstoned: this.widgetRegistration.isTombstoned,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
        });
    }
    async createWidgetPlatformInstance(pinned) {
        // If the widget is added from the platform, it needs to be instantiated
        if (this.isPlatform) {
            Measures.logEvent(dashboard, "AddPlatformWidgetStarted", 16777216 /* productAndServicePerformance */, {
                appId: this.widgetRegistration.appId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetDefinitionId,
            });
            let hostWidgetId = null;
            let failureResult = 0;
            try {
                hostWidgetId = await new Promise(async (resolveWidgetId, rejectWidgetId) => {
                    // Tell the widget to construct its platform instance
                    let hostInstanceManager = await getHostInstanceManagerApi();
                    if (hostInstanceManager) {
                        await hostInstanceManager.addWidgetAsync(this.widgetRegistration.id, pinned, (hresult, hostWidgetId) => {
                            failureResult = hresult;
                            if (hresult === 0 && hostWidgetId && hostWidgetId !== "") {
                                resolveWidgetId(hostWidgetId);
                            }
                            else {
                                rejectWidgetId((hresult === 0) ? "NoWidgetId" : "HResult");
                            }
                        });
                    }
                    else {
                        rejectWidgetId("MissingProviderAPI");
                    }
                });
            }
            catch (error) {
                Measures.logEvent(dashboard, "AddPlatformWidgetFailed", 16777216 /* productAndServicePerformance */, {
                    appId: this.widgetRegistration.appId,
                    hresult: failureResult,
                    providerId: this.widgetRegistration.providerId,
                    exception: error,
                    reason: "Exception",
                    widgetDefinitionId: this.widgetDefinitionId,
                });
                return;
            }
            // Store the widgetId for future use.
            if (hostWidgetId) {
                Measures.logEvent(dashboard, "AddPlatformWidgetSucceeded", 16777216 /* productAndServicePerformance */, {
                    appId: this.widgetRegistration.appId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetDefinitionId,
                    widgetInstanceId: hostWidgetId,
                });
                this.widgetId = hostWidgetId;
            }
            else {
                // Should have thrown if this was the case
                Measures.logEvent(dashboard, "AddPlatformWidgetFailed", 16777216 /* productAndServicePerformance */, {
                    appId: this.widgetRegistration.appId,
                    hresult: 0x8000ffff,
                    providerId: this.widgetRegistration.providerId,
                    reason: "NoWidgetId",
                    widgetDefinitionId: this.widgetDefinitionId,
                });
            }
        }
    }
    // For use by the instance manager to serialize the widget, so don't expose in either IWidgetInstance or IWidgetInstanceInternal
    get cachedProperties() {
        return this._cachedProperties;
    }
    get widgetRegistration() {
        return this._widgetRegistration;
    }
    set widgetId(widgetId) {
        // When the widget is created in the platform, it will provide the unique widgetId set here
        // All 3rd party platform widget instances will have a unique guid widgetId
        this._widgetId = widgetId;
    }
    set widgetInstanceId(widgetInstanceId) {
        this._widgetInstanceId = widgetInstanceId;
    }
    get widgetId() {
        // For legacy use If this widget instance does not have a platform generated widgetId as a local member, its id is the registration.id
        // All 3rd party platform widget instances will have a unique guid widgetId
        return this._widgetId ? this._widgetId : this._widgetRegistration.id;
    }
    get widgetInstanceId() {
        return this._widgetInstanceId ? this._widgetInstanceId : this._widgetRegistration.id;
    }
    get widgetDefinitionId() {
        // The definition id is always the registration of the type
        return this._widgetRegistration.id;
    }
    get accountId() {
        return this._accountId;
    }
    set accountId(accountId) {
        this._cachedProperties.set("accountId", accountId);
        this._accountId = accountId;
    }
    get instanceId() {
        return this._instanceId;
    }
    get indexedDBInstanceId() {
        return this._indexedDBInstanceId;
    }
    get entryPoint() {
        return this._widgetRegistration.entryPoint;
    }
    get widgetDesignation() {
        return this._widgetDesignation;
    }
    get widgetState() {
        return this._widgetState;
    }
    set widgetState(state) {
        // If the widget state isn't changing then don't take any action.
        if (this._widgetState === state) {
            return;
        }
        let validTransition = false;
        if (this._widgetState === "unknown") {
            // valid transitions: loading
            if (state === "loading") {
                validTransition = true;
            }
        }
        else if (this._widgetState === "loading") {
            // valid transitions: normal, error
            if (state === "normal" || state === "error") {
                validTransition = true;
            }
        }
        else if (this._widgetState === "normal") {
            // valid transitions: loading, error
            if (state === "loading" || state === "error") {
                validTransition = true;
            }
        }
        else if (this._widgetState === "error") {
            // valid transitions: loading
            // error -> normal is vaid when the widget became ready after timeout
            if (state === "loading" || state === "normal") {
                validTransition = true;
            }
        }
        // If we transition to a non error state, reset the error state reason and error code
        if (state !== "error") {
            this.widgetErrorStateReason = "";
            this.widgetErrorCode = 0;
        }
        this.logWidgetInit(state, validTransition);
        if (validTransition) {
            let previousState = this._widgetState;
            this._widgetState = state;
            if (state === "loading") {
                this._loadingCompletePromise = new Promise((resolve) => (this._loadingCompletePromiseResolver = resolve));
            }
            else if (previousState === "loading") {
                // If the widget is leaving the loading state and there is an active "load complete" promise, then resolve it.
                Trace.logEvent(dashboard, "WidgetSignalingLoadComplete", 1 /* information */, {
                    appId: this.widgetRegistration.appId,
                    isTombstoned: this.widgetRegistration.isTombstoned,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
                this._loadingCompletePromiseResolver();
                this._loadingCompletePromise = null;
                this._loadingCompletePromiseResolver = null;
            }
            // Once the widget is done loading either by being in a good or bad state, reset softRefreshRequested so we no longer
            // fire WidgetInit on every state change
            if (state === "normal" || state === "error") {
                this._softRefreshRequested = false;
            }
            // Now that we have finished modifying the internal state, notify callers that the state has changed. This prevents
            // re-entrant state changes, which can happen from onStateChange callback handlers.
            try {
                this.onStateChanged(this, previousState, this._widgetState);
            }
            catch (error) {
                Measures.logEvent(dashboard, "WidgetStateChangedEventError", 16777216 /* productAndServicePerformance */, {
                    appId: this.widgetRegistration.appId,
                    failureMessage: error === null || error === void 0 ? void 0 : error.message,
                    failureName: error === null || error === void 0 ? void 0 : error.name,
                    isTombstoned: this.widgetRegistration.isTombstoned,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            }
        }
        else {
            throw new Error("Invalid widgetState transition from " + this._widgetState + " to " + state);
        }
    }
    get widgetIconUri() {
        return this._widgetIconUri;
    }
    get widgetErrorStateReason() {
        return this._widgetErrorStateReason;
    }
    set widgetErrorStateReason(state) {
        this._widgetErrorStateReason = state;
    }
    get widgetErrorCode() {
        return this._widgetErrorCode;
    }
    set widgetErrorCode(state) {
        this._widgetErrorCode = state;
    }
    get telemetry() {
        return this._telemetry;
    }
    get softRefreshRequested() {
        return this._softRefreshRequested;
    }
    get isPlatform() {
        return this.widgetRegistration.entryPoint.type === "PLATFORM";
    }
    get boardView() {
        return this._boardView;
    }
    set boardView(boardView) {
        this._boardView = boardView;
    }
    set softRefreshRequested(softRefreshRequested) {
        Trace.logEvent(dashboard, "SoftRefreshRequested", 0 /* verbose */, {
            softRefreshRequested: softRefreshRequested,
        });
        log.info("softRefreshRequested " + { softRefreshRequested });
        this._softRefreshRequested = softRefreshRequested;
    }
    get isLoadForAddedInstancePending() {
        return this._isLoadForAddedInstancePending;
    }
    set isLoadForAddedInstancePending(isLoadForAddedInstancePending) {
        this._isLoadForAddedInstancePending = isLoadForAddedInstancePending;
    }
    get isCustomElementDefined() {
        return this._isCustomElementDefined;
    }
    set isCustomElementDefined(isCustomElementDefined) {
        this._isCustomElementDefined = isCustomElementDefined;
    }
    // FUTURE: pattern to store/get versioned apis...
    tryGetApi(apiName, minVersion, targetVersion) {
        let apiQuery = apiName.toLowerCase();
        if (this._apiObjects.has(apiQuery)) {
            if (!(this._apiObjects.get(apiQuery) instanceof WidgetApi)) {
                // Initialize on demand
                this._apiObjects.set(apiQuery, this._apiObjects[apiQuery](this));
            }
            return this._apiObjects.get(apiQuery);
        }
        return null;
    }
    // Internal mock override
    setApi(apiName, version, createApi) {
        let apiKey = apiName.toLowerCase();
        this._apiObjects.set(apiKey, createApi);
    }
    getWidgetProperty(key) {
        return this._cachedProperties.get(key);
    }
    getAllWidgetProperties() {
        return this._cachedProperties;
    }
    async setWidgetProperty(key, value) {
        Measures.logEvent(dashboard, "AttemptedSetWidgetProperty", 16777216 /* productAndServicePerformance */, {
            appId: this.widgetRegistration.appId,
            indexedDBInstanceId: this.instanceId,
            property: key,
            providerId: this.widgetRegistration.providerId,
            widgetDefinitionId: this.widgetRegistration.id,
            widgetInstanceId: this.widgetId,
        });
        this._cachedProperties.set(key, value);
        let orchestrator = (await getWidgetOrchestrator());
        return orchestrator.updateWidgetInstance(this);
    }
    async updateRegistration(registration) {
        this._widgetRegistration = registration;
        // Check to see if the current size isn't supported with the new registration and reset us to the preferred size.
        const currentSize = this._cachedProperties.get("size");
        const isSizeSupported = registration.supportedSizes.includes(currentSize);
        if (!isSizeSupported && this._widgetId) {
            const widgetModelApi = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("model", 1, 1);
            widgetModelApi === null || widgetModelApi === void 0 ? void 0 : widgetModelApi.setPinnedWidgetSize(this._widgetId, this._instanceId, registration.preferredSize);
        }
        const propertiesApi = this.tryGetApi("properties", 1, 1);
        propertiesApi === null || propertiesApi === void 0 ? void 0 : propertiesApi.onRegistrationChanged();
        return Promise.resolve();
    }
    async refreshContent() {
        if (this._widgetState !== "error") {
            Trace.logEvent(dashboard, "WidgetInstanceRefreshingContent", 1 /* information */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this.instanceId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
            let refreshPromise;
            const lifecycleApi = this.tryGetApi("lifecycle", 1, 1);
            if (lifecycleApi) {
                refreshPromise = lifecycleApi.onRefresh();
            }
            else {
                refreshPromise = Promise.resolve();
            }
            return refreshPromise
                .then(() => {
                Trace.logEvent(dashboard, "WidgetInstanceRefreshComplete", 1 /* information */, {
                    appId: this.widgetRegistration.appId,
                    indexedDBInstanceId: this.instanceId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            })
                .catch((err) => {
                Trace.logEvent(dashboard, "WidgetInstanceRefreshFailed", 3 /* error */, {
                    appId: this.widgetRegistration.appId,
                    errorMessage: err === null || err === void 0 ? void 0 : err.message,
                    errorName: err === null || err === void 0 ? void 0 : err.name,
                    indexedDBInstanceId: this.instanceId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            });
        }
        else {
            Trace.logEvent(dashboard, "WidgetInstanceRefreshSkipped", 0 /* verbose */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this.instanceId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
        }
    }
    async suspend() {
        if (this._widgetState !== "error") {
            Trace.logEvent(dashboard, "WidgetInstanceSuspending", 1 /* information */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this.instanceId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
            // on suspend the widget will not be visible
            const visibilityApi = this.tryGetApi("visibility", 1, 1);
            if (visibilityApi) {
                await visibilityApi.onHidden();
            }
            let suspendingPromise;
            const lifecycleApi = this.tryGetApi("lifecycle", 1, 1);
            if (lifecycleApi) {
                suspendingPromise = lifecycleApi.onSuspending();
            }
            else {
                suspendingPromise = Promise.resolve();
            }
            if (this._widgetState === "loading") {
                Trace.logEvent(dashboard, "WidgetInstanceSuspendLoading", 1 /* information */, {
                    appId: this.widgetRegistration.appId,
                    indexedDBInstanceId: this.instanceId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            }
            else if (this._widgetState === "unknown") {
                Trace.logEvent(dashboard, "WidgetInstanceSuspendUnknownState", 1 /* information */, {
                    appId: this.widgetRegistration.appId,
                    indexedDBInstanceId: this.instanceId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            }
            // For now, give all widgets a fixed 1 second grace period before suspending, even if they don't ask for it.
            return Promise.all([this._loadingCompletePromise, suspendingPromise, new Promise((resolve) => setTimeout(resolve, 1000))])
                .then(() => {
                Trace.logEvent(dashboard, "WidgetInstanceSuspendComplete", 1 /* information */, {
                    appId: this.widgetRegistration.appId,
                    indexedDBInstanceId: this.instanceId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            })
                .catch((err) => {
                Trace.logEvent(dashboard, "WidgetInstanceSuspendFailed", 3 /* error */, {
                    appId: this.widgetRegistration.appId,
                    errorMessage: err.message,
                    errorName: err.name,
                    indexedDBInstanceId: this.instanceId,
                    providerId: this.widgetRegistration.providerId,
                    widgetDefinitionId: this.widgetRegistration.id,
                    widgetInstanceId: this.widgetId,
                });
            });
        }
        else {
            Trace.logEvent(dashboard, "WidgetInstanceSuspendSkipped", 0 /* verbose */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this.instanceId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
        }
    }
    async resume() {
        if (this._widgetState !== "error") {
            Trace.logEvent(dashboard, "WidgetInstanceResumed", 1 /* information */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this.instanceId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
            // on resume assume that the widget is visible so that it can receive updates from its data provider
            const visibilityApi = this.tryGetApi("visibility", 1, 1);
            if (visibilityApi) {
                await visibilityApi.onVisible();
            }
            const lifecycleApi = this.tryGetApi("lifecycle", 1, 1);
            if (lifecycleApi) {
                await lifecycleApi.onResumed().catch((err) => {
                    Trace.logEvent(dashboard, "WidgetInstanceResumeFailed", 3 /* error */, {
                        appId: this.widgetRegistration.appId,
                        errorMessage: err.message,
                        errorName: err.name,
                        indexedDBInstanceId: this.instanceId,
                        providerId: this.widgetRegistration.providerId,
                        widgetDefinitionId: this.widgetRegistration.id,
                        widgetInstanceId: this.widgetId,
                    });
                });
            }
        }
        else {
            Trace.logEvent(dashboard, "WidgetInstanceResumeSkipped", 0 /* verbose */, {
                appId: this.widgetRegistration.appId,
                indexedDBInstanceId: this.instanceId,
                providerId: this.widgetRegistration.providerId,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
        }
    }
    addExecuteActionListener(listener) {
        this._executeActionListeners.push(listener);
    }
    removeExecuteActionListener(listener) {
        let index = this._executeActionListeners.indexOf(listener);
        if (index > -1) {
            this._executeActionListeners.splice(index, 1);
        }
    }
    addSubmitActionListener(listener) {
        this._submitActionListeners.push(listener);
    }
    removeSubmitActionListener(listener) {
        let index = this._submitActionListeners.indexOf(listener);
        if (index > -1) {
            this._submitActionListeners.splice(index, 1);
        }
    }
    addStateChangeListener(listener) {
        this._stateChangeListeners.push(listener);
    }
    removeStateChangeListener(listener) {
        let index = this._stateChangeListeners.indexOf(listener);
        if (index > -1) {
            this._stateChangeListeners.splice(index, 1);
        }
    }
    signalAPIInitialization() {
        this._loadingStartedPromiseResolver();
        this._loadingStartedPromise = null;
    }
    async widgetInitializationPromise() {
        if (this._loadingStartedPromise) {
            await this._loadingStartedPromise;
        }
    }
    isOutlookWidget() {
        return this.widgetId === Constants.CalendarWidgetId || this.widgetId === Constants.ToDoWidgetId;
    }
    notifyExecuteAction(actionProps) {
        this._executeActionListeners.forEach((listener) => {
            listener(actionProps, this.instanceId, this.widgetInstanceId, this.widgetRegistration);
        });
    }
    async notifySubmitAction(actionProps) {
        this._submitActionListeners.forEach((listener) => {
            listener(actionProps, this.instanceId, this.widgetInstanceId, this.widgetRegistration);
        });
    }
    onStateChanged(widgetInstance, previousState, currentState) {
        Trace.logEvent(dashboard, "WebAppWidgetInstanceStateChange", 0 /* verbose */, {
            appId: this.widgetRegistration.appId,
            currentState: currentState,
            indexedDBInstanceId: this.instanceId,
            isTombstoned: this.widgetRegistration.isTombstoned,
            previousState: previousState,
            providerId: this.widgetRegistration.providerId,
            widgetDefinitionId: this.widgetRegistration.id,
            widgetInstanceId: this.widgetId,
        });
        log.debug("## state ## has ## changed ##  [", previousState, "] [", currentState, "]  => ", this._stateChangeListeners, this.widgetRegistration.id);
        if (currentState === "loading" && this._isLoadForAddedInstancePending) {
            this._isLoadForAddedInstancePending = false;
            // A non-existent url indicates that the widget is peregrine-based. For Peregrine widgets where load is pending after
            // added via addWidgetInstance, a call to loadPerigrineWidgets needs to be made at this point to actually begin loading
            // the widget content.
            if (typeof this.widgetRegistration.entryPoint.host === "undefined") {
                getWidgetOrchestrator().then((orchestrator) => {
                    orchestrator.loadPeregrineWidget(this);
                });
            }
        }
        let numStateChangeListeners = this._stateChangeListeners.length; // Freeze this value in case a callback removes itself
        for (let index = 0; index < this._stateChangeListeners.length; index++) {
            if (this._stateChangeListeners[index]) {
                this._stateChangeListeners[index](this, previousState, currentState);
                if (numStateChangeListeners > this._stateChangeListeners.length) {
                    index--; // Only support removal of itself, no further removals allowed in callback
                    numStateChangeListeners--;
                }
            }
        }
    }
    logWidgetInit(state, validTransition) {
        // We only care about full/hard refreshes; if a hard refresh passes or fails, the soft refresh will likely follow suit.
        // Additionally: full refreshes will ocurr far less frequently than soft refreshes
        if (!this._softRefreshRequested) {
            let partCPayload = {
                appId: this.widgetRegistration.appId,
                currentState: this.widgetState,
                errorStateCode: this.widgetErrorCode,
                errorStateReason: this.widgetErrorStateReason,
                indexedDBInstanceId: this.instanceId,
                isReauthRequiredUserEnabled: true,
                isTombstoned: this.widgetRegistration.isTombstoned,
                isSignedOutUserEnabled: true,
                newState: state,
                providerId: this.widgetRegistration.providerId,
                validTransition: validTransition,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            };
            Measures.logEvent(dashboard, "WidgetInit", 16777216 /* productAndServicePerformance */, partCPayload);
            DispatchOnly.logEvent(dashboard, "WidgetInit", 0 /* requiredServiceData */, 16777216 /* productAndServicePerformance */, partCPayload);
        }
        else {
            Trace.logEvent(dashboard, "WidgetRefresh", 1 /* information */, {
                appId: this.widgetRegistration.appId,
                currentState: this.widgetState,
                errorStateCode: this.widgetErrorCode,
                errorStateReason: this.widgetErrorStateReason,
                indexedDBInstanceId: this.instanceId,
                isReauthRequiredUserEnabled: true,
                isTombstoned: this.widgetRegistration.isTombstoned,
                isSignedOutUserEnabled: true,
                newState: state,
                providerId: this.widgetRegistration.providerId,
                validTransition: validTransition,
                widgetDefinitionId: this.widgetRegistration.id,
                widgetInstanceId: this.widgetId,
            });
        }
    }
}
